// Printing and the cash drawer on a desk device (spec/10 §10, spec/09 §6.2, spec/13 §11; F-STN-13, F-OPS-08,
// F-OPS-09, F-FNB-05).
//
// A **print document** is what the platform prints: a short list of lines — text, an amount, an item, a rule, a
// feed, a QR — laid out for a 58 or 80 mm roll. The server renders every job (a kitchen ticket, a receipt, a test
// page) into one, and each transport prints the same document its own way:
//
//   desk     the desk device (Clavian Counter) turns it into ESC/POS for its printer's width — money in Indian
//            grouping, "Rs 1,23,456.00", because a receipt printer's code page has no ₹ — and sends the bytes over
//            TCP 9100 or raw through the Windows spooler (`station.print`).
//   cloud    the printer collects the job by polling (Star CloudPRNT, Epson Server Direct Print); the server lays
//            the document out as plain text for it.
//   browser  the console lays it out as a print-ready 58/80 mm page.
//
// `station.print` carries the document itself (`format: 'document'`) rather than pre-rendered bytes: the desk device
// knows its printer, and one agreed document keeps the three transports printing the same ticket. `escpos` (raw
// bytes in `data`, base64) stays in the contract for a job that is already ESC/POS; `pdf` is refused by the desk
// device as unsupported until a PDF path is built (spec/10 §10, 2026-10-01).
//
// Nested objects carry `.meta({ id })` so the station's generator names each record (tools/src/codegen/realtime.ts).
import { z } from "../zod.js";
import { PrintJobIdSchema } from "../ids/schema.js";
/** The roll widths a receipt or kitchen printer takes (spec/10 §10). */
export const PRINT_PAPER_WIDTHS = [58, 80];
export function isPrintPaperWidth(value) {
    return PRINT_PAPER_WIDTHS.includes(value);
}
// An integer checked against the two widths rather than a union of literals: the clients' generators take a
// plain integer (tools/src/codegen), and the server refuses any other width.
export const PrintPaperWidthSchema = z
    .int()
    .refine(isPrintPaperWidth, { message: 'The paper width is 58 or 80 mm.' })
    .openapi('PrintPaperWidth', { description: 'The paper roll width in millimetres: 58 or 80 (spec/10 §10).' });
/** The kinds of line a print document is made of. */
export const PRINT_LINE_KINDS = ['text', 'amount', 'item', 'rule', 'feed', 'qr'];
/** How a line's text sits on the roll. */
export const PRINT_ALIGNS = ['left', 'center', 'right'];
/**
 * How big a line prints: `normal`; `tall` (double height, the totals line); `large` (double width and height —
 * a kitchen ticket's order number, "#0421", F-FNB-05 rule 1).
 */
export const PRINT_SIZES = ['normal', 'tall', 'large'];
/** An amount on a print line: integer minor units and the ISO 4217 code, as everywhere (CLAUDE.md). */
export const PrintAmountSchema = z
    .object({
    amount: z.int(),
    currency: z.string().length(3),
})
    .meta({ id: 'PrintAmount' });
/**
 * One line of a print document. Flat rather than a union, so every consumer — the station's generated record
 * among them — reads it without a discriminator:
 *
 *   text    `text`, with `align`, `bold` and `size`
 *   amount  `text` on the left and `amount` on the right ("Total ........ Rs 250.00"); `bold`, `size`
 *   item    `quantity` × `text`, `amount` on the right when it has one, and `notes` (modifiers, a note) under it
 *   rule    a dashed line across the roll
 *   feed    `lines` blank lines
 *   qr      a QR code of `text` (the invoice link), centred
 */
export const PrintLineSchema = z
    .object({
    kind: z.enum(PRINT_LINE_KINDS),
    text: z.string().max(500).optional(),
    align: z.enum(PRINT_ALIGNS).optional(),
    bold: z.boolean().optional(),
    size: z.enum(PRINT_SIZES).optional(),
    amount: PrintAmountSchema.optional(),
    quantity: z.int().min(1).max(999).optional(),
    notes: z.array(z.string().max(200)).max(50).optional(),
    lines: z.int().min(1).max(10).optional(),
})
    .meta({ id: 'PrintLine', description: 'One line of a print document (spec/13 §11).' });
/** A print document: the lines, the roll it is laid out for, and whether the printer cuts after it. */
export const PrintDocumentSchema = z
    .object({
    paperWidth: PrintPaperWidthSchema,
    lines: z.array(PrintLineSchema).max(500),
    cut: z.boolean(),
})
    .meta({ id: 'PrintDocument', description: 'What a print job prints, laid out by each transport (spec/13 §11, spec/10 §10).' });
/** How a desk device reaches a printer: ESC/POS over TCP 9100, or raw through a Windows spooler queue (spec/10 §10). */
export const DESK_PRINTER_TRANSPORTS = ['tcp', 'spooler'];
/** The printer a `station.print` or `station.drawer.open` is for, as the desk device reaches it. */
export const StationPrinterTargetSchema = z
    .object({
    transport: z.enum(DESK_PRINTER_TRANSPORTS),
    /** The printer's address for `tcp` (an IP or a host name on the venue network); null for `spooler`. */
    host: z.string().min(1).max(255).nullable(),
    /** The TCP port for `tcp` — 9100 unless the printer says otherwise; null for `spooler`. */
    port: z.int().min(1).max(65535).nullable(),
    /** The Windows printer (spooler queue) name for `spooler`; null for `tcp`. */
    queueName: z.string().min(1).max(255).nullable(),
    paperWidth: PrintPaperWidthSchema,
})
    .meta({ id: 'StationPrinterTarget' });
/** What `station.print` carries: `document` or raw bytes, and the formats of spec/09 §6.2. */
export const STATION_PRINT_FORMATS = ['document', 'escpos', 'pdf'];
/**
 * `station.print` (spec/09 §6.2): **desk mode only**; expires after 5 minutes; acked when printed. A gaming
 * station answers `ok: false` with `not_desk_device` (F-STN-13 rule 4). The ack's `result` is a
 * `StationPrintResult`; a failure's `error.code` is one of `STATION_PRINT_ERROR_CODES`.
 */
export const StationPrintPayloadSchema = z
    .object({
    jobId: PrintJobIdSchema,
    printer: StationPrinterTargetSchema,
    format: z.enum(STATION_PRINT_FORMATS),
    /** The print document, for `format: 'document'`; null otherwise. */
    document: PrintDocumentSchema.nullable(),
    /** The raw job, base64, for `escpos` or `pdf`; null for `document`. */
    data: z.string().max(1_000_000).nullable(),
})
    .openapi('StationPrintPayload', { description: '`station.print`: print a job on a desk device’s printer (spec/09 §6.2).' });
/** The ESC/POS drawer-kick pin: most drawers are wired to pin 2 (`ESC p 0`), some to pin 5 (`ESC p 1`). */
export const DRAWER_KICK_PINS = [2, 5];
export function isDrawerKickPin(value) {
    return DRAWER_KICK_PINS.includes(value);
}
export const DrawerKickPinSchema = z
    .int()
    .refine(isDrawerKickPin, { message: 'The drawer kick pin is 2 or 5.' })
    .openapi('DrawerKickPin', { description: 'The ESC/POS drawer-kick pin: 2 (most drawers) or 5.' });
/**
 * `station.drawer.open` (spec/09 §6.2): **desk mode only**; expires after 30 seconds. The desk device sends the
 * kick pulse through the receipt printer the drawer is wired to (spec/10 §10, F-OPS-09 rule 2).
 */
export const StationDrawerOpenPayloadSchema = z
    .object({
    printer: StationPrinterTargetSchema,
    pin: DrawerKickPinSchema,
})
    .openapi('StationDrawerOpenPayload', { description: '`station.drawer.open`: kick the cash drawer (spec/09 §6.2).' });
/** What a desk device's ack of `station.print` says in `result`: the job, printed, and how many bytes went out. */
export const StationPrintResultSchema = z
    .object({
    jobId: PrintJobIdSchema,
    status: z.enum(['printed']),
    bytes: z.int().min(0),
})
    .openapi('StationPrintResult', { description: 'What a desk device’s `station.print` ack reports (spec/09 §6.2).' });
/**
 * The `error.code`s a desk device answers a print or drawer command with. `not_desk_device` is a gaming station
 * refusing (F-STN-13 rule 4); `printer_unreachable` and `spooler_error` mean the printer is offline — the api raises
 * `printer_offline` and falls back (spec/13 §11); the rest are a job the device could not read.
 */
export const STATION_PRINT_ERROR_CODES = [
    'not_desk_device',
    'printer_unreachable',
    'spooler_error',
    'unsupported_format',
    'invalid_payload',
];
/** Whether a desk device's failure means the printer itself is offline (rather than a bad job or a wrong device). */
export function isPrinterOfflineError(code) {
    return code === 'printer_unreachable' || code === 'spooler_error';
}
//# sourceMappingURL=printing.js.map